import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useAuth } from '@/auth/AuthProvider'
import { FxAlert, FxButton, FxIcon, FxSpinner, FxTextField } from '@/components/fx'
import { accessApi, lookupUserByEmail } from '@/lib/api'
import type { FolderRole } from '@/lib/api/access'

import { ProjektDialog } from './ProjektDialog'
import {
  FOLDER_ROLE_LABELS,
  FOLDER_ROLE_ORDER,
  identitiesFromTemplateAccess,
  memberRows,
} from './folderMembers'
import type { ProjectNode } from './projectData'

/**
 * The kebab's "Freigeben".
 *
 * Deliberately the small version: who can reach this project, add one person
 * by e-mail, change or withdraw a role. The full share surface
 * (`Freigeben.dc.html`, link sharing, per-form grants) is a later wave and
 * will want `memberRows`, the role vocabulary and this dialog's copy — which
 * is why those sit in `folderMembers.ts` rather than inside the component.
 *
 * There are no e-mail invitations: an address with no account is a 404
 * (`user_not_found`) and is reported as such rather than quietly accepted.
 */
export function ShareFolderDialog({
  open,
  project,
  onOpenChange,
}: {
  open: boolean
  project: ProjectNode | null
  onOpenChange: (open: boolean) => void
}) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const folderId = project?.folder.id ?? ''
  // One form in the project is enough to resolve every member's identity —
  // see `identitiesFromTemplateAccess`.
  const probeTemplateId = project?.templates[0]?.id

  const members = useQuery({
    queryKey: ['folderMembers', folderId],
    queryFn: () => accessApi.folderMembers(folderId),
    enabled: open && folderId !== '',
  })

  const identities = useQuery({
    queryKey: ['folderMemberIdentities', folderId, probeTemplateId],
    queryFn: async () => {
      const access = await accessApi.get(probeTemplateId as string)
      return identitiesFromTemplateAccess(access.entries, folderId)
    },
    enabled: open && folderId !== '' && probeTemplateId != null,
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['folderMembers', folderId] })

  const setMember = useMutation({
    mutationFn: ({ uid, role }: { uid: string; role: FolderRole }) =>
      accessApi.setFolderMember(folderId, uid, role),
    onSuccess: refresh,
  })
  const removeMember = useMutation({
    mutationFn: (uid: string) => accessApi.removeFolderMember(folderId, uid),
    onSuccess: refresh,
  })

  const [email, setEmail] = useState('')
  const [role, setRole] = useState<FolderRole>('contributor')
  const invite = useMutation({
    mutationFn: async () => {
      const found = await lookupUserByEmail(email.trim())
      if (!found) throw new Error('user_not_found')
      await accessApi.setFolderMember(folderId, found.uid, role)
    },
    onSuccess: () => {
      setEmail('')
      void refresh()
    },
  })

  const rows = memberRows(members.data ?? {}, identities.data ?? new Map(), user?.id)
  const writeError = setMember.error ?? removeMember.error

  return (
    <ProjektDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Projekt freigeben"
      description={
        project ? (
          <>Wer „{project.folder.name}“ öffnen darf, sieht auch die Formulare darin.</>
        ) : undefined
      }
      footer={
        <FxButton variant="outlined" onClick={() => onOpenChange(false)}>
          Fertig
        </FxButton>
      }
    >
      <form
        className="flex flex-wrap items-end gap-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (!email.trim() || invite.isPending) return
          invite.mutate()
        }}
      >
        <FxTextField
          containerClassName="min-w-[220px] flex-1"
          label="E-Mail-Adresse"
          type="email"
          autoComplete="off"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <label className="flex flex-col gap-1 text-small">
          <span className="text-body">Rolle</span>
          <select
            className="h-control rounded-md bg-surface px-3 text-body text-text shadow-raised"
            value={role}
            onChange={(event) => setRole(event.target.value as FolderRole)}
          >
            {FOLDER_ROLE_ORDER.map((value) => (
              <option key={value} value={value}>
                {FOLDER_ROLE_LABELS[value]}
              </option>
            ))}
          </select>
        </label>
        <FxButton type="submit" disabled={!email.trim() || invite.isPending}>
          Hinzufügen
        </FxButton>
      </form>

      {invite.isError && (
        <FxAlert tone="danger">
          {(invite.error as Error).message === 'user_not_found'
            ? 'Zu dieser E-Mail-Adresse gibt es kein Konto. Einladungen per E-Mail sind noch nicht möglich.'
            : 'Die Person konnte nicht hinzugefügt werden.'}
        </FxAlert>
      )}

      {members.isPending && <FxSpinner label="Freigaben werden geladen …" />}
      {members.isError && <FxAlert tone="danger">Die Freigaben konnten nicht geladen werden.</FxAlert>}
      {writeError && <FxAlert tone="danger">Die Änderung wurde nicht gespeichert.</FxAlert>}

      {members.isSuccess && (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {rows.map((row) => (
            <li key={row.uid} className="flex flex-wrap items-center gap-4 py-2">
              <FxIcon name="person" size={22} filled className="flex-none text-primary-tonal" />
              <span className="min-w-0 flex-1">
                <span className="block truncate">
                  {row.label}
                  {row.isSelf && ' (Sie)'}
                </span>
                {row.secondary && (
                  <span className="block truncate text-small text-text-muted">{row.secondary}</span>
                )}
                {row.unresolved && (
                  <span className="block text-small text-text-muted">
                    Name und E-Mail sind für dieses Projekt nicht abrufbar.
                  </span>
                )}
              </span>
              <label className="flex items-center gap-2">
                <span className="sr-only">Rolle von {row.label}</span>
                <select
                  className="h-control rounded-md bg-surface px-3 text-body text-text shadow-raised"
                  value={row.role}
                  disabled={row.isSelf || setMember.isPending}
                  onChange={(event) =>
                    setMember.mutate({ uid: row.uid, role: event.target.value as FolderRole })
                  }
                >
                  {FOLDER_ROLE_ORDER.map((value) => (
                    <option key={value} value={value}>
                      {FOLDER_ROLE_LABELS[value]}
                    </option>
                  ))}
                </select>
              </label>
              {!row.isSelf && (
                <button
                  type="button"
                  className="flex h-9 w-9 flex-none items-center justify-center rounded-sm text-danger-text"
                  aria-label={`Zugriff von ${row.label} entfernen`}
                  disabled={removeMember.isPending}
                  onClick={() => removeMember.mutate(row.uid)}
                >
                  <FxIcon name="person_remove" size={22} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </ProjektDialog>
  )
}
